import * as React from 'react';
import {
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
} from '@fluentui/react';
import { ApiHelper } from '../../../ApiHelper';
import type { Service } from '../../../interfaces/Service';
import type {
    AgentTaskWorkItemDetailResult,
    AgentTaskWorkItemSummary,
} from '../types';
import {
    buildAgencyLogsArchivePageUrl,
    buildAgencyLogsPageUrl,
    buildAgencySessionUrl,
    buildAdoPullRequestUrl,
    buildJobDefinitionUrl,
    formatDate,
    formatNumber,
    getJobStatusLabel,
    getPullRequestStatusWithHealthLabel,
    parseUtcDate,
} from '../helpers';
import { buildAgentTaskQuestionsPageUrl } from '../questionsApi';
import { buildAgentTaskProposedChangesPageUrl } from '../proposedChangesApi';
import { normalizeAgentTaskWorkItemDetail } from '../workItemApi';

const ACTIVE_JOB_STATUSES = new Set([0, 1, 2, 5, 6, 7]);

function parseTime(value?: string | null) {
    return parseUtcDate(value)?.getTime();
}

function getExecutionTime(item: AgentTaskWorkItemSummary) {
    return parseTime(item.agentEndDate)
        ?? parseTime(item.agentStartDate)
        ?? Number.NEGATIVE_INFINITY;
}

export function sortJobExecutionsNewestFirst(items: AgentTaskWorkItemSummary[]) {
    return [...items].sort((left, right) =>
        getExecutionTime(right) - getExecutionTime(left)
        || right.workItemId - left.workItemId
        || (right.attemptNumber ?? 0) - (left.attemptNumber ?? 0),
    );
}

function getCurrentExecutionIds(items: AgentTaskWorkItemSummary[]) {
    const currentByJob = new Map<string, number>();
    sortJobExecutionsNewestFirst(items).forEach(item => {
        if (!currentByJob.has(item.jobName)) {
            currentByJob.set(item.jobName, item.workItemId);
        }
    });
    return new Set(currentByJob.values());
}

function formatMinutes(minutes: number) {
    return `${minutes} min`;
}

export function formatExecutionDuration(
    startDate?: string | null,
    endDate?: string | null,
    active = false,
    now: Date | number = Date.now(),
) {
    const start = parseTime(startDate);
    if (start === undefined) return 'Unavailable';

    if (endDate) {
        const end = parseTime(endDate);
        if (end === undefined || end < start) return 'Unavailable';
        return formatMinutes(Math.round((end - start) / 60_000));
    }

    if (!active) return 'Unavailable';

    const current = now instanceof Date ? now.getTime() : now;
    if (!Number.isFinite(current) || current < start) return 'Unavailable';
    return `${formatMinutes(Math.round((current - start) / 60_000))} (running)`;
}

/** Displays a persisted UTC execution timestamp in the browser's labeled local timezone. */
export function formatExecutionDate(value?: string | null) {
    return formatDate(value);
}

function formatAicCost(value: number) {
    if (Math.abs(value) < 10) {
        return value.toLocaleString(undefined, { maximumSignificantDigits: 3 });
    }
    return Math.round(value).toLocaleString();
}

function formatShutdownStats(value: string) {
    try {
        return {
            content: JSON.stringify(JSON.parse(value), null, 2),
            error: undefined,
        };
    } catch (error) {
        return {
            content: value,
            error: error instanceof Error ? error.message : 'Invalid JSON.',
        };
    }
}

export function TaskExecutionLinks({
    serviceName,
    service,
    taskListId,
    taskId,
    item,
    showLabel = true,
}: {
    serviceName: string;
    service: Service | undefined;
    taskListId: number;
    taskId: number;
    item: AgentTaskWorkItemSummary;
    showLabel?: boolean;
}) {
    const agencyUrl = buildAgencySessionUrl(item.agencyJobId);
    const prUrl = buildAdoPullRequestUrl(service, item.prId);
    const hasQuestions = (item.openQuestionCount ?? 0)
        + (item.answeredQuestionCount ?? 0)
        + (item.declinedQuestionCount ?? 0) > 0;
    const links = [
        agencyUrl && (
            <a key="agency" href={agencyUrl} target="_blank" rel="noopener noreferrer">Agency</a>
        ),
        item.agencyLogsAvailable && (
            <a
                key="logs"
                href={buildAgencyLogsPageUrl(serviceName, taskListId, taskId, item.workItemId)}
            >
                Logs
            </a>
        ),
        item.agencyLogsArchiveAvailable && (
            <a
                key="archive"
                href={buildAgencyLogsArchivePageUrl(serviceName, taskListId, taskId, item.workItemId)}
            >
                Archive
            </a>
        ),
        prUrl && (
            <a key="pr" href={prUrl} target="_blank" rel="noopener noreferrer">PR #{item.prId}</a>
        ),
        hasQuestions && (
            <a
                key="questions"
                href={buildAgentTaskQuestionsPageUrl(serviceName, {
                    taskListId,
                    taskId,
                    workItemId: item.workItemId,
                })}
            >
                Full questions page
            </a>
        ),
    ].filter(Boolean) as React.ReactElement[];

    if (links.length === 0) {
        return null;
    }

    return (
        <span>
            {showLabel && <strong>Links: </strong>}
            {links.map((link, index) => (
                <React.Fragment key={link.key}>
                    {index > 0 && ' • '}
                    {link}
                </React.Fragment>
            ))}
        </span>
    );
}

function getLineage(item: AgentTaskWorkItemSummary) {
    const lineage: string[] = [];
    if (item.previousWorkItemId) lineage.push(`Previous #${item.previousWorkItemId}`);
    if (item.repairOfWorkItemId) lineage.push(`Repair of #${item.repairOfWorkItemId}`);
    return lineage.length > 0 ? lineage.join(' • ') : 'First execution';
}

function ExecutionDiagnostics({
    detail,
    serviceName,
}: {
    detail: AgentTaskWorkItemDetailResult;
    serviceName: string;
}) {
    const shutdownStats = React.useMemo(
        () => detail.shutdownStats?.trim() ? formatShutdownStats(detail.shutdownStats) : undefined,
        [detail.shutdownStats],
    );
    const outputs = [
        ['Output1', detail.output1],
        ['Output2', detail.output2],
    ].filter((entry): entry is [string, string] => !!entry[1]?.trim());

    return (
        <Stack tokens={{ childrenGap: 12 }} styles={{ root: { padding: '0 12px 12px' } }}>
            <div>
                <a href={buildJobDefinitionUrl(serviceName, detail.jobName)}>Job definition</a>
                {' • '}
                <a href={buildAgentTaskProposedChangesPageUrl(serviceName, {
                    taskListId: detail.taskListId,
                    taskId: detail.taskId,
                    workItemId: detail.workItemId,
                })}
                >
                    Full proposed changes page
                </a>
            </div>
            <section aria-label="Execution usage">
                <Text variant="mediumPlus">Execution usage</Text>
                <div style={{ marginTop: 4, lineHeight: 1.5 }}>
                    Model: {detail.modelUsed || '-'}
                    {' • '}Tokens: {formatNumber(detail.tokensConsumed)}
                    {detail.aiu !== undefined && detail.aiu !== null && (
                        <> • AIC: {formatAicCost(detail.aiu)}</>
                    )}
                </div>
            </section>

            {detail.error?.trim() && (
                <MessageBar messageBarType={MessageBarType.error}>{detail.error}</MessageBar>
            )}

            {shutdownStats && (
                <section aria-label="Shutdown statistics">
                    <Text variant="mediumPlus">Shutdown statistics</Text>
                    {shutdownStats.error && (
                        <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { marginTop: 6 } }}>
                            Stored shutdown statistics are not valid JSON: {shutdownStats.error}
                        </MessageBar>
                    )}
                    <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', margin: '6px 0 0', padding: 10, background: '#f8f8f8' }}>
                        {shutdownStats.content}
                    </pre>
                </section>
            )}

            {detail.resolvedDefinitionJson?.trim() && (
                <section aria-label="Resolved job definition">
                    <Text variant="mediumPlus">Resolved job definition</Text>
                    <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', margin: '6px 0 0', padding: 10, background: '#f8f8f8' }}>
                        {detail.resolvedDefinitionJson}
                    </pre>
                </section>
            )}

            {outputs.length > 0 && (
                <section aria-label="Raw additional outputs">
                    <Text variant="mediumPlus">Raw additional outputs</Text>
                    {outputs.map(([label, value]) => (
                        <div key={label}>
                            <Text variant="small">{label}</Text>
                            <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', margin: '4px 0 8px', padding: 10, background: '#f8f8f8' }}>
                                {value}
                            </pre>
                        </div>
                    ))}
                </section>
            )}
        </Stack>
    );
}

export interface TaskJobExecutionsProps {
    serviceName: string;
    service: Service | undefined;
    taskListId: number;
    taskId: number;
    workItems: AgentTaskWorkItemSummary[];
    onWorkItemUpdated: (item: AgentTaskWorkItemSummary) => void;
}

export function TaskJobExecutions({
    serviceName,
    service,
    taskListId,
    taskId,
    workItems,
    onWorkItemUpdated,
}: TaskJobExecutionsProps) {
    const sortedItems = React.useMemo(
        () => sortJobExecutionsNewestFirst(workItems),
        [workItems],
    );
    const currentIds = React.useMemo(() => getCurrentExecutionIds(workItems), [workItems]);
    const [expandedId, setExpandedId] = React.useState<number | null>(null);
    const [details, setDetails] = React.useState<Record<number, AgentTaskWorkItemDetailResult>>({});
    const [loadingIds, setLoadingIds] = React.useState<Set<number>>(() => new Set());
    const [errors, setErrors] = React.useState<Record<number, string>>({});
    const requestedIds = React.useRef<Set<number>>(new Set());

    React.useEffect(() => {
        setExpandedId(null);
        setDetails({});
        setLoadingIds(new Set());
        setErrors({});
        requestedIds.current.clear();
    }, [serviceName, taskId, taskListId]);

    async function loadDetail(workItemId: number, retry = false) {
        if (retry) {
            requestedIds.current.delete(workItemId);
        }
        if (details[workItemId] || requestedIds.current.has(workItemId)) return;

        requestedIds.current.add(workItemId);
        setLoadingIds(current => new Set(current).add(workItemId));
        setErrors(current => {
            const next = { ...current };
            delete next[workItemId];
            return next;
        });
        try {
            const response = await ApiHelper(
                `/api/service/${serviceName}/agenttasklists/${taskListId}/tasks/${taskId}/workitems/${workItemId}`,
            );
            const detail = normalizeAgentTaskWorkItemDetail(response);
            setDetails(current => ({ ...current, [workItemId]: detail }));
            onWorkItemUpdated(detail);
        } catch (error) {
            setErrors(current => ({
                ...current,
                [workItemId]: error instanceof Error
                    ? error.message
                    : 'Execution diagnostics are unavailable.',
            }));
        } finally {
            setLoadingIds(current => {
                const next = new Set(current);
                next.delete(workItemId);
                return next;
            });
        }
    }

    function toggleDiagnostics(workItemId: number) {
        const opening = expandedId !== workItemId;
        setExpandedId(opening ? workItemId : null);
        if (opening) void loadDetail(workItemId, !!errors[workItemId]);
    }

    React.useEffect(() => {
        sortedItems.forEach(item => {
            void loadDetail(item.workItemId);
        });
    }, [sortedItems]);

    return (
        <Stack tokens={{ childrenGap: 8 }} styles={{ root: { marginTop: 12 } }}>
            {sortedItems.map(item => {
                const expanded = expandedId === item.workItemId;
                const current = currentIds.has(item.workItemId);
                const active = ACTIVE_JOB_STATUSES.has(item.jobStatus);

                return (
                    <article
                        key={item.workItemId}
                        aria-label={`${item.jobName} attempt ${item.attemptNumber ?? 'unknown'}`}
                        style={{
                            border: '1px solid #d2d0ce',
                            borderLeft: current ? '4px solid #0078d4' : '1px solid #d2d0ce',
                            borderRadius: 6,
                            background: '#ffffff',
                        }}
                    >
                        <div style={{ padding: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                <div>
                                    <h3 style={{ display: 'inline', margin: 0, fontSize: 16, fontWeight: 600 }}>
                                        {item.jobName}
                                    </h3>
                                    <span> • Attempt {item.attemptNumber ?? '?'}</span>
                                    {current && (
                                        <span style={{ marginLeft: 8, color: '#0078d4', fontWeight: 600 }}>
                                            Current
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    aria-expanded={expanded}
                                    aria-controls={`execution-diagnostics-${item.workItemId}`}
                                    onClick={() => toggleDiagnostics(item.workItemId)}
                                >
                                    {expanded ? 'Hide diagnostics' : 'Show diagnostics'}
                                </button>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '4px 16px', marginTop: 8, lineHeight: 1.5 }}>
                                <span>Started: {formatExecutionDate(item.agentStartDate)}</span>
                                <span>
                                    Duration: {formatExecutionDuration(
                                        item.agentStartDate,
                                        item.agentEndDate,
                                        active,
                                    )}
                                </span>
                                <span>Status: <strong>{getJobStatusLabel(item.jobStatus)}</strong></span>
                                <span>
                                    PR: <strong>
                                        {getPullRequestStatusWithHealthLabel(
                                            item.pullRequestStatus,
                                            item.pullRequestHealth,
                                            item.prId !== undefined && item.prId !== null,
                                        )}
                                    </strong>
                                </span>
                                <span>Snapshot #{item.snapshotId} • Work item #{item.workItemId}</span>
                                <span>Gain: {formatNumber(item.estimatedGain)}</span>
                                <span>
                                    Cost: {loadingIds.has(item.workItemId)
                                        ? 'Loading...'
                                        : details[item.workItemId]?.aiu !== undefined
                                            && details[item.workItemId]?.aiu !== null
                                            ? `${formatAicCost(details[item.workItemId].aiu)} AIC`
                                            : '-'}
                                </span>
                                <span>Lineage: {getLineage(item)}</span>
                            </div>

                            <div style={{ marginTop: 8 }}>
                                <TaskExecutionLinks
                                    serviceName={serviceName}
                                    service={service}
                                    taskListId={taskListId}
                                    taskId={taskId}
                                    item={item}
                                />
                            </div>
                        </div>

                        {expanded && (
                            <div id={`execution-diagnostics-${item.workItemId}`}>
                                {loadingIds.has(item.workItemId) && (
                                    <Spinner
                                        size={SpinnerSize.medium}
                                        label="Loading execution diagnostics..."
                                        styles={{ root: { padding: 12 } }}
                                    />
                                )}
                                {errors[item.workItemId] && (
                                    <MessageBar
                                        messageBarType={MessageBarType.warning}
                                        styles={{ root: { margin: '0 12px 12px' } }}
                                    >
                                        {errors[item.workItemId]}
                                    </MessageBar>
                                )}
                                {details[item.workItemId] && (
                                    <ExecutionDiagnostics
                                        detail={details[item.workItemId]}
                                        serviceName={serviceName}
                                    />
                                )}
                            </div>
                        )}
                    </article>
                );
            })}
        </Stack>
    );
}
